import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useGetIssueById, useMessages, useResolveIssue } from "../../queries/helpDesk";
import RichTextEditor from "../../components/Forms/RichTextEditor";
import { ActionButton } from "../../components/Forms/ActionButton";
import { useSendMessage } from "../../queries/helpDesk";
import { toast, ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { useHeader } from "../../context/HeaderContext";

const UserChat = () => {
  const { id } = useParams();
  const [replyMessage, setReplyMessage] = useState("");

  const { setHeaderTitle } = useHeader();
  const navigate = useNavigate();

  // need to check messages fetch schema then implement
  const { mutateAsync: sendMessage } = useSendMessage(id);
  const { data: messages, isLoading, error } = useMessages(id);
  const { data: chatDetails, isLoading: isChatDetailsLoading } = useGetIssueById(id);
  const { mutate: resolveIssue, isPending: isResolving } = useResolveIssue();

  const handleSendReply = (e) => {
    e.preventDefault();
    if (replyMessage.trim()) {
      // Send message with admin messageBy since this is admin panel
      const messageData = {
        message: replyMessage,
        messageBy: "admin",
        userType: "admin",
        media: ""
      };
      sendMessage(messageData);
      setReplyMessage("");
    }
  };

  const handleResolveIssue = () => {
    resolveIssue(
      { id },
      {
        onSuccess: (res) => {
          if (res.error) {
            toast.error(res.error || "Failed to resolve issue. Please try again.");
            return;
          }
          toast.success("Issue resolved successfully.");
          setTimeout(() => {
            navigate(-1);
          }, 2000);
        },
        onError: (err) => {
          console.error("Error resolving issue:", err);
          toast.error("Failed to resolve issue. Please try again.");
        }
      }
    );
  };

  // Helper function to format timestamp
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
  };

  // Helper function to get sender name
  const getSenderName = (message) => {
    if (message.messageBy === "admin") {
      return "Climate Crusaders";
    }
    return chatDetails?.createdBy?.username; // In real implementation, you might get this from user data
  };

  // Use real data if available, otherwise use mock data
  const displayData = chatDetails?.messages;

  useEffect(() => {
    setHeaderTitle('Help Desk - User Chat');
    return () => {
      setHeaderTitle('');
    };
  }, [setHeaderTitle]);

  if (isChatDetailsLoading || isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Loading chat...</div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex w-full flex-col px-4 pt-5 pb-14 bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)]">
        {/* Header */}
        <div className="border-b border-gray-200 pb-4 mb-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-xl font-bold text-gray-900 mb-2">
                Help Desk - {chatDetails?.createdBy?.username}
              </h1>
              <h2 className="text-base font-semibold text-gray-800 mb-1">
                {chatDetails?.title ?? '-'}
              </h2>
              <p className="text-base text-gray-600">
                <span className="font-semibold">Help Desk - Concern :</span> {chatDetails?.concern}
              </p>
            </div>
          </div>
        </div>

        {/* Issue Details */}
        <div className="mb-6">
          <h3 className="text-base font-semibold text-gray-900 mb-1">Details :</h3>
          <p className="text-gray-700 text-base leading-relaxed mb-4">
            {chatDetails?.description}
          </p>

          {/* Images */}
          {chatDetails.images && chatDetails.images.length > 0 ? (
            <div className="flex gap-4 mb-4">
              {chatDetails.images.map((image, index) => (
                <div key={index} className="relative">
                  <img
                    src={image}
                    alt={`Attachment ${index + 1}`}
                    className="w-48 h-36 object-cover rounded-lg border border-gray-200 cursor-pointer"
                    onClick={() => window.open(image, "_blank")}
                  />
                  {/* Checkmark overlay */}
                  {/* <div className="absolute top-2 right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div> */}
                </div>
              ))}
            </div>
          ) : (
            <div>
              <p className="text-base">No Images Provided!</p>
            </div>
          )}
        </div>

        {/* Chat Messages */}
        <div className="space-y-4 mb-6 rounded-lg p-4">
          {displayData.map((message) => (
            <div key={message._id || message.id} className="border-gray-100 pb-4">
              <div className="flex items-start gap-3">
                {/* Avatar */}
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-sm font-semibold">
                    {message.messageBy === "admin" ? 'CC' : getSenderName(message).charAt(0)}
                  </span>
                </div>

                {/* Message Content */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-">{getSenderName(message)}</span>
                    <span className="text-base">
                      {formatTimestamp(message.createdAt)}
                    </span>
                  </div>

                  <div className="border-t py-2">
                    {message.messageBy === 'admin' ? (
                      <div
                        className="text-base"
                        dangerouslySetInnerHTML={{ __html: message.message }}
                      />
                    ) : (
                      <p className="text-base whitespace-pre-line">
                        {message.message}
                      </p>
                    )}

                    {/* Message Image/Media */}
                    {message.media && (
                      <div className="mt-3">
                        <img
                          src={message.media}
                          alt="Message attachment"
                          className="w-48 h-36 object-cover rounded-lg border border-navBtn"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Reply Section */}
        <div className="border-gray-200 pt-6">
          <h3 className="text-base font-semibold text-gray-900 mb-4">Reply Here :</h3>

          <form onSubmit={handleSendReply}>
            <div className="mb-4">
              <RichTextEditor
                value={replyMessage}
                onChange={setReplyMessage}
                placeholder="Type your reply message here..."
              />
            </div>

            <div className="flex justify-end gap-3">
              <ActionButton
                variant="white"
                text="Resolve Issue Conversation"
                handleSubmit={handleResolveIssue}
                disabled={isResolving}
              />
              <ActionButton
                variant="submit"
                text="Send"
                handleSubmit={handleSendReply}
                disabled={!replyMessage.trim()}
              />
            </div>
          </form>
        </div>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default UserChat;